import axios from 'axios';
import TokenService from './tokenService';
import { BASE_API_URL } from '../constants';

const instance = axios.create({
    baseURL : BASE_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

instance.interceptors.request.use(
    (config) => {
        const method = (config.method || 'get').toLowerCase();

        // Add cache-busting param to GET requests so list endpoints always return fresh data.
        if (method === 'get' && !config.skipCacheBust) {
            if (config.params instanceof URLSearchParams) {
                config.params.set('_t', String(Date.now()));
            } else {
                config.params = {
                    ...(config.params || {}),
                    _t: Date.now(),
                };
            }
        }

        if (config.skipAuthHeader) {
            return config;
        }

        const token = TokenService.getLocalAccessToken();
        if (token && config.url !== '/login/' && config.url !== '/refresh-token/' && config.url !== '/register/') {
            config.headers['Authorization'] = 'Bearer ' + token;
        }
        return config;
    },
    ((error) => {
        return Promise.reject(error);
    })
);

instance.interceptors.response.use(
    (res) => {
        return res;
    },
    async (err) => {
        const originalConfig = err.config;
        const skipAuthRedirect = Boolean(originalConfig?.skipAuthRedirect);

        // Don't intercept errors from login, register, or refresh-token endpoints
        if (originalConfig.url === '/login/' || 
            originalConfig.url === '/register/' || 
            originalConfig.url === '/refresh-token/') {
            return Promise.reject(err);
        }

        if (err.response) {
            if (err.response.status === 401 && !originalConfig._retry) {
                if (skipAuthRedirect) {
                    return Promise.reject(err);
                }

                const authHeader = originalConfig.headers?.Authorization || originalConfig.headers?.authorization;
                const currentUser = TokenService.getUser();
                const hasStoredSession = Boolean(currentUser?.access || currentUser?.refresh);

                // Public/anonymous requests can legitimately return 401.
                // Do not redirect to login unless there is an auth session to recover.
                if (!authHeader && !hasStoredSession) {
                    return Promise.reject(err);
                }

                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalConfig.headers['Authorization'] = 'Bearer ' + token;
                        return instance(originalConfig);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalConfig._retry = true;
                isRefreshing = true;
                const loginPath = currentUser?.isCustomer ? '/customer-login' : '/login';

                try {
                    const refreshToken = TokenService.getLocalRefreshToken();
                    
                    // If no refresh token exists, redirect to login
                    if (!refreshToken) {
                        console.error('No refresh token available');
                        processQueue(new Error('No refresh token'), null);
                        TokenService.removeUser();
                        window.location = loginPath;
                        return Promise.resolve();
                    }

                    const { data } = await instance.post('/refresh-token/', {
                        refresh: refreshToken,
                    });
                    const { access } = data;
                    
                    // Validate the response contains a valid access token
                    if (!access) {
                        console.error('Invalid refresh token response - no access token received');
                        processQueue(new Error('Invalid refresh response'), null);
                        TokenService.removeUser();
                        window.location = loginPath;
                        return Promise.resolve();
                    }
                    
                    TokenService.updateLocalAccessToken(access);
                    instance.defaults.headers.common['Authorization'] = 'Bearer ' + access;
                    processQueue(null, access);
                    return instance(originalConfig);
                } catch (error) {
                    // Redirect to login for any authentication-related errors
                    // This includes 401/403 (invalid token) and network/server errors during refresh
                    console.error('Token refresh failed:', error);
                    processQueue(error, null);
                    TokenService.removeUser();
                    window.location = loginPath;
                    // Return a resolved promise to prevent error from bubbling up during redirect
                    return Promise.resolve();
                } finally {
                    isRefreshing = false;
                }
            }
        }

        return Promise.reject(err);
    }
);

export default instance;